import React, { useContext, useEffect, useMemo, useState } from 'react';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';
import { CategoryContext } from '../../providers/CategoryContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { showError, showSuccess } from '../../utils/sweetAlert';

const emptyImage = { url: '', label: '', isActive: true };
const CATEGORY_SCOPE = '__category__';

const normalizeExtraImages = (images = []) => (
  Array.isArray(images)
    ? images.map(image => (
      typeof image === 'string'
        ? { url: image, label: '', isActive: true }
        : { url: image?.url || '', label: image?.label || '', isActive: image?.isActive !== false }
    )).filter(image => image.url)
    : []
);

const CategoryExtraImagesManager = () => {
  const { categories, refreshCategories } = useContext(CategoryContext);
  const [localCategories, setLocalCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedScope, setSelectedScope] = useState(CATEGORY_SCOPE);
  const [enabled, setEnabled] = useState(false);
  const [extraImages, setExtraImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  const selectedCategory = useMemo(
    () => localCategories.find(category => category._id === selectedCategoryId),
    [localCategories, selectedCategoryId]
  );

  const selectedSubcategory = useMemo(() => {
    if (!selectedCategory || selectedScope === CATEGORY_SCOPE) return null;
    return (selectedCategory.subcategories || []).find(sub => sub.name === selectedScope) || null;
  }, [selectedCategory, selectedScope]);

  const subcategories = selectedCategory?.subcategories || [];

  const activeImagesCount = extraImages.filter(image => image.url && image.isActive !== false).length;
  const configuredScopesCount = localCategories.reduce((total, category) => {
    let count = 0;
    if (category.productExtraImagesEnabled && normalizeExtraImages(category.productExtraImages).some(image => image.isActive !== false)) {
      count += 1;
    }
    (category.subcategories || []).forEach(sub => {
      if (sub.productExtraImagesEnabled && normalizeExtraImages(sub.productExtraImages).some(image => image.isActive !== false)) {
        count += 1;
      }
    });
    return total + count;
  }, 0);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && localCategories.length === 0) {
      setLocalCategories(categories);
    }
  }, [categories, localCategories.length]);

  useEffect(() => {
    if (!selectedCategory) return;

    if (selectedScope !== CATEGORY_SCOPE) {
      const stillExists = (selectedCategory.subcategories || []).some(sub => sub.name === selectedScope);
      if (!stillExists) {
        setSelectedScope(CATEGORY_SCOPE);
        return;
      }
    }

    if (selectedSubcategory) {
      setEnabled(Boolean(selectedSubcategory.productExtraImagesEnabled));
      setExtraImages(normalizeExtraImages(selectedSubcategory.productExtraImages));
      return;
    }

    setEnabled(Boolean(selectedCategory.productExtraImagesEnabled));
    setExtraImages(normalizeExtraImages(selectedCategory.productExtraImages));
  }, [selectedCategory, selectedSubcategory, selectedScope]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/categories?includeInactive=true`);
      if (!response.ok) throw new Error('Error al cargar categorías');
      const data = await response.json();
      setLocalCategories(Array.isArray(data) ? data : []);
      if (!selectedCategoryId && data?.length) {
        setSelectedCategoryId(data[0]._id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showError('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const addEmptyImage = () => {
    setExtraImages(prev => [...prev, { ...emptyImage }]);
  };

  const addMediaImage = (mediaItem) => {
    const imageUrl = mediaItem.url || mediaItem.filename || '';
    if (!imageUrl) return;

    setExtraImages(prev => {
      if (prev.some(image => image.url === imageUrl)) return prev;
      return [
        ...prev,
        {
          url: imageUrl,
          label: mediaItem.originalname || '',
          isActive: true
        }
      ];
    });
  };

  const updateImage = (index, field, value) => {
    setExtraImages(prev => prev.map((image, imageIndex) => (
      imageIndex === index ? { ...image, [field]: value } : image
    )));
  };

  const removeImage = (index) => {
    setExtraImages(prev => prev.filter((_, imageIndex) => imageIndex !== index));
  };

  const moveImage = (index, direction) => {
    setExtraImages(prev => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const nextImages = [...prev];
      [nextImages[index], nextImages[nextIndex]] = [nextImages[nextIndex], nextImages[index]];
      return nextImages;
    });
  };

  const handleSave = async () => {
    if (!selectedCategory) {
      showError('Selecciona una categoría');
      return;
    }

    const normalizedImages = extraImages
      .map(image => ({
        url: image.url.trim(),
        label: image.label?.trim() || '',
        isActive: image.isActive !== false
      }))
      .filter(image => image.url);

    if (enabled && normalizedImages.length === 0) {
      showError('Agrega al menos una imagen o desactiva la sección');
      return;
    }

    setSaving(true);

    try {
      const nextSubcategories = (selectedCategory.subcategories || []).map(sub => {
        if (selectedScope === CATEGORY_SCOPE || sub.name !== selectedScope) {
          return sub;
        }
        return {
          ...sub,
          productExtraImagesEnabled: enabled,
          productExtraImages: normalizedImages
        };
      });

      const payload = {
        name: selectedCategory.name,
        description: selectedCategory.description || '',
        image: selectedCategory.image || '',
        subcategories: nextSubcategories,
        isActive: selectedCategory.isActive,
        productExtraImagesEnabled: selectedScope === CATEGORY_SCOPE
          ? enabled
          : Boolean(selectedCategory.productExtraImagesEnabled),
        productExtraImages: selectedScope === CATEGORY_SCOPE
          ? normalizedImages
          : normalizeExtraImages(selectedCategory.productExtraImages)
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/categories/${selectedCategory._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al guardar imágenes');
      }

      const updatedCategory = await response.json();
      setLocalCategories(prev => prev.map(category => (
        category._id === updatedCategory._id ? updatedCategory : category
      )));
      refreshCategories();
      showSuccess(
        selectedScope === CATEGORY_SCOPE
          ? 'Imágenes informativas de la categoría guardadas'
          : `Imágenes informativas de ${selectedScope} guardadas`
      );
    } catch (error) {
      console.error('Error saving category extra images:', error);
      showError(error.message || 'Error al guardar imágenes');
    } finally {
      setSaving(false);
    }
  };

  const scopeLabel = selectedSubcategory
    ? `${selectedCategory?.name} · ${selectedSubcategory.name}`
    : (selectedCategory?.name || 'esta categoría');

  return (
    <div className="catimg-panel">
      <div className="catmgr-header">
        <div className="catmgr-header-text">
          <h1>Imágenes por Categoría</h1>
          <p>Agrega imágenes informativas al final de la galería de cada producto. También podés configurarlas por subcategoría (ej. Niños · Canguros).</p>
        </div>
        <button className="catmgr-add-btn" onClick={handleSave} disabled={saving || loading}>
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      <div className="catmgr-kpi-row">
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
            <i className="fas fa-tags"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{localCategories.length}</span>
            <span className="catmgr-kpi-label">Categorías</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{configuredScopesCount}</span>
            <span className="catmgr-kpi-label">Configuradas</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
            <i className="fas fa-images"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{activeImagesCount}</span>
            <span className="catmgr-kpi-label">Activas aquí</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
            <i className="fas fa-toggle-on"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{enabled ? 'On' : 'Off'}</span>
            <span className="catmgr-kpi-label">Estado</span>
          </div>
        </div>
      </div>

      <div className="catimg-editor">
        <div className="catimg-settings">
          <div className="catmgr-form-field">
            <label>Categoría</label>
            <select
              value={selectedCategoryId}
              onChange={(event) => {
                setSelectedCategoryId(event.target.value);
                setSelectedScope(CATEGORY_SCOPE);
              }}
              disabled={loading}
            >
              <option value="">Seleccionar categoría</option>
              {localCategories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}{category.isActive ? '' : ' (inactiva)'}
                </option>
              ))}
            </select>
          </div>

          <div className="catmgr-form-field">
            <label>Ámbito</label>
            <select
              value={selectedScope}
              onChange={(event) => setSelectedScope(event.target.value)}
              disabled={!selectedCategory || loading}
            >
              <option value={CATEGORY_SCOPE}>Toda la categoría</option>
              {subcategories.map(sub => (
                <option key={sub._id || sub.name} value={sub.name}>
                  Subcategoría: {sub.name}
                </option>
              ))}
            </select>
          </div>

          <div className="catimg-toggle-row">
            <div>
              <strong>Mostrar imágenes al final del producto</strong>
              <span>
                Cuando está activo, estas imágenes se agregan automáticamente a la galería de los productos de {scopeLabel}.
                {selectedSubcategory
                  ? ' Tiene prioridad sobre las imágenes generales de la categoría.'
                  : (subcategories.length > 0 ? ' Las subcategorías pueden sobrescribir esta configuración.' : '')}
              </span>
            </div>
            <label className="catmgr-toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
                disabled={!selectedCategory}
              />
              <span className="catmgr-toggle-slider"></span>
            </label>
          </div>

          <div className="catimg-actions">
            <button type="button" className="catmgr-submit-btn" onClick={() => setShowMediaLibrary(true)} disabled={!selectedCategory}>
              <i className="fas fa-photo-film"></i> Elegir de biblioteca
            </button>
            <button type="button" className="catmgr-cancel-btn" onClick={addEmptyImage} disabled={!selectedCategory}>
              <i className="fas fa-link"></i> Agregar URL
            </button>
          </div>
        </div>

        <div className="catimg-list">
          {extraImages.length === 0 ? (
            <div className="catmgr-empty catimg-empty">
              <i className="fas fa-images"></i>
              <h3>Sin imágenes informativas</h3>
              <p>Selecciona imágenes de la biblioteca o pega URLs para {scopeLabel}</p>
            </div>
          ) : (
            extraImages.map((image, index) => (
              <div key={`${image.url}-${index}`} className={`catimg-item ${image.isActive === false ? 'disabled' : ''}`}>
                <div className="catimg-preview">
                  {image.url ? (
                    <img src={getDisplayImageUrl(image.url)} alt={image.label || `Imagen ${index + 1}`} />
                  ) : (
                    <i className="fas fa-image"></i>
                  )}
                </div>
                <div className="catimg-fields">
                  <div className="catmgr-form-field">
                    <label>Imagen {index + 1}</label>
                    <input
                      type="text"
                      value={image.url}
                      onChange={(event) => updateImage(index, 'url', event.target.value)}
                      placeholder="URL o ruta de la imagen"
                    />
                  </div>
                  <div className="catmgr-form-field">
                    <label>Etiqueta interna</label>
                    <input
                      type="text"
                      value={image.label}
                      onChange={(event) => updateImage(index, 'label', event.target.value)}
                      placeholder="Ej: tabla de medidas, mensaje final"
                    />
                  </div>
                </div>
                <div className="catimg-item-actions">
                  <button type="button" onClick={() => moveImage(index, -1)} disabled={index === 0} title="Subir">
                    <i className="fas fa-chevron-up"></i>
                  </button>
                  <button type="button" onClick={() => moveImage(index, 1)} disabled={index === extraImages.length - 1} title="Bajar">
                    <i className="fas fa-chevron-down"></i>
                  </button>
                  <button
                    type="button"
                    className={image.isActive === false ? 'catimg-muted' : ''}
                    onClick={() => updateImage(index, 'isActive', image.isActive === false)}
                    title={image.isActive === false ? 'Activar' : 'Desactivar'}
                  >
                    <i className={`fas ${image.isActive === false ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                  <button type="button" className="catimg-delete" onClick={() => removeImage(index)} title="Eliminar">
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showMediaLibrary && (
        <div className="catimg-modal-overlay" onClick={() => setShowMediaLibrary(false)}>
          <div className="catimg-modal" onClick={(event) => event.stopPropagation()}>
            <div className="catimg-modal-header">
              <h3>Seleccionar imágenes</h3>
              <button type="button" onClick={() => setShowMediaLibrary(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <EnhancedMediaLibrary mode="selection" onSelect={addMediaImage} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryExtraImagesManager;
